import { useState } from 'react'
import { CreditCard, Plus, Trash2 } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

import { useLabel } from '@/lib/labels'

import {
  MAX_DESCRIPTION_LENGTH,
  MAX_NAME_LENGTH,
  activeToggleRequest,
  channelHint,
  formFromPaymentType,
  nextPosition,
  toPaymentTypeRequest,
  validatePaymentTypeForm,
  type PaymentTypeFormState,
} from './paymentTypeForm'
import {
  paymentTypeErrorMessage,
  useCreatePaymentType,
  useDeletePaymentType,
  usePaymentTypeTemplate,
  usePaymentTypes,
  useUpdatePaymentType,
  type CaptureMode,
  type PaymentType,
  type PaymentTypeTemplate,
} from './paymentTypesApi'

/**
 * Settings → Payment types. Where collected money goes (a bank account, cash,
 * a payment link) and through which channels. Gated server-side by
 * PAYMENT_TYPE_VIEW / PAYMENT_TYPE_MANAGE — no client-side guard, same
 * convention as every other admin page: a 403 shows up as an inline message.
 *
 * <p>Seeded ("System") types can be renamed, re-mapped and deactivated but
 * never deleted: app builds that still send only a channel code rely on them
 * to resolve a type. A custom type with collections against it cannot be
 * deleted either — deactivating is how anything is retired.</p>
 */
export function PaymentTypesPage() {
  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
          Settings
        </p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
          <CreditCard className="h-5 w-5" /> Payment types
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Where money is collected to — a bank account, cash, a payment link —
          and through which channels. The agent picks the payment type first,
          then one of its channels; the payment mode decides whether they fill
          in the capture form or raise a link.
        </p>
      </div>

      <PaymentTypesSection />
    </div>
  )
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

function PaymentTypesSection() {
  const [showInactive, setShowInactive] = useState(false)
  const types = usePaymentTypes(showInactive)
  const template = usePaymentTypeTemplate()
  const toggle = useUpdatePaymentType()
  const del = useDeletePaymentType()
  const label = useLabel()
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<PaymentType | null>(null)

  const actionError = del.isError
    ? paymentTypeErrorMessage(del.error, 'Delete failed.')
    : toggle.isError
      ? paymentTypeErrorMessage(toggle.error, 'Update failed.')
      : null

  function onToggleActive(t: PaymentType) {
    const question = t.active
      ? `Deactivate ${t.name}? Agents stop seeing it at once; collections already taken against it are unchanged.`
      : `Activate ${t.name}? Agents see it again at once.`
    if (window.confirm(question)) {
      toggle.mutate({ paymentTypeId: t.paymentTypeId, body: activeToggleRequest(t) })
    }
  }

  function onDelete(t: PaymentType) {
    if (
      window.confirm(
        `Delete ${t.name}? This cannot be undone. If anything was ever collected against it the server will refuse — deactivate it instead.`,
      )
    ) {
      del.mutate(t.paymentTypeId)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Payment types</CardTitle>
            <CardDescription>
              {types.data ? `${types.data.length} payment types` : '—'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-navy-900">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
              Show inactive
            </label>
            <Button
              size="sm"
              onClick={() => {
                setEditing(null)
                setShowCreate(true)
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Add payment type
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {(showCreate || editing) && (
          <div className="border-b border-slate-200 px-5 py-4">
            {template.data ? (
              <PaymentTypeForm
                // Re-initialise when Edit is clicked on a different row.
                key={editing?.paymentTypeId ?? 'new'}
                existing={editing}
                template={template.data}
                defaultPosition={nextPosition(types.data ?? [])}
                onClose={() => {
                  setShowCreate(false)
                  setEditing(null)
                }}
              />
            ) : template.isError ? (
              <p className="text-sm text-negative">
                Couldn't load the channel list — PAYMENT_TYPE_VIEW is required.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">Loading…</p>
            )}
          </div>
        )}

        {actionError && (
          <div className="px-5 pt-4">
            <Alert variant="destructive">
              <AlertTitle>{del.isError ? 'Could not delete' : 'Could not update'}</AlertTitle>
              <AlertDescription>{actionError}</AlertDescription>
            </Alert>
          </div>
        )}

        {types.isPending && <SkeletonRows rows={4} cols={7} />}
        {types.isError && (
          <div className="px-5 pb-4 text-sm text-negative">
            Couldn't load payment types — PAYMENT_TYPE_VIEW is required.
          </div>
        )}
        {types.data && types.data.length === 0 && (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-medium text-navy-900">
              {showInactive ? 'No payment types yet' : 'No active payment types'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {showInactive
                ? 'Click "Add payment type" to create the first one.'
                : 'Agents cannot collect until at least one Manual type is active. Tick "Show inactive" to bring one back, or add a new one.'}
            </p>
          </div>
        )}
        {types.data && types.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                <tr>
                  <th className="px-5 py-2 font-medium">Payment type</th>
                  <th className="px-3 py-2 font-medium">Payment mode</th>
                  <th className="px-3 py-2 font-medium">Channel types</th>
                  <th className="px-3 py-2 font-medium">Applicable for</th>
                  <th className="px-3 py-2 font-medium">Position</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-5 py-2 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {types.data.map((t) => (
                  <tr key={t.paymentTypeId} className="border-b border-slate-200 last:border-0 hover:bg-navy-50">
                    <td className="px-5 py-2">
                      <div className="font-medium text-navy-900">{t.name}</div>
                      {t.description && (
                        <div className="text-2xs text-muted-foreground">{t.description}</div>
                      )}
                      <div className="mt-0.5 flex items-center gap-1.5">
                        <span className="font-mono text-xs text-muted-foreground">{t.code}</span>
                        {t.isSystem && <Badge tone="navy">System</Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={t.captureMode === 'LINK' ? 'info' : 'navy'}>
                        {label('captureMode', t.captureMode)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {/* Badge, not Chip — a Chip renders a <button>, and these are not actions. */}
                      <div className="flex flex-wrap gap-1">
                        {t.channels.map((c) => (
                          <Badge key={c} tone="slate">{label('channelType', c)}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-navy-900">
                      {t.applicableFor.map((a) => label('applicableFor', a)).join(', ')}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-navy-900">{t.position}</td>
                    <td className="px-3 py-2">
                      <Badge tone={t.active ? 'positive' : 'slate'}>{t.active ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td className="px-5 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowCreate(false)
                            setEditing(t)
                          }}
                        >
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => onToggleActive(t)} disabled={toggle.isPending}>
                          {t.active ? 'Deactivate' : 'Activate'}
                        </Button>
                        {/* Seeded types are never deleted — the server would 409
                            payment_type_is_system — so the button is not offered. */}
                        {!t.isSystem && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => onDelete(t)}
                            disabled={del.isPending}
                            aria-label={`Delete ${t.name}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Form — the doc's fields, nothing more. The code is derived server-side from
// the name and shown read-only once it exists.
// ---------------------------------------------------------------------------

function PaymentTypeForm({
  existing,
  template,
  defaultPosition,
  onClose,
}: {
  existing: PaymentType | null
  template: PaymentTypeTemplate
  defaultPosition: number
  onClose: () => void
}) {
  const create = useCreatePaymentType()
  const update = useUpdatePaymentType()
  const [form, setForm] = useState<PaymentTypeFormState>(() => formFromPaymentType(existing, defaultPosition))

  function patch(p: Partial<PaymentTypeFormState>) {
    setForm((f) => ({ ...f, ...p }))
  }

  function toggleCode(key: 'channels' | 'applicableFor', code: string) {
    setForm((f) => {
      const cur = f[key]
      const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]
      return key === 'channels' ? { ...f, channels: next } : { ...f, applicableFor: next }
    })
  }

  const errors = validatePaymentTypeForm(form)
  const pending = create.isPending || update.isPending
  const canSubmit = errors.length === 0 && !pending

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    const body = toPaymentTypeRequest(form)
    if (existing) {
      update.mutate({ paymentTypeId: existing.paymentTypeId, body }, { onSuccess: onClose })
    } else {
      create.mutate(body, { onSuccess: onClose })
    }
  }

  const errorMsg = create.isError
    ? paymentTypeErrorMessage(create.error, 'Save failed.')
    : update.isError
      ? paymentTypeErrorMessage(update.error, 'Save failed.')
      : null

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <Label htmlFor="pt-name">Payment type</Label>
          <Input
            id="pt-name"
            value={form.name}
            onChange={(e) => patch({ name: e.target.value })}
            placeholder="HDFC Bank"
            maxLength={MAX_NAME_LENGTH}
            required
            autoFocus
          />
          {existing ? (
            <p className="mt-1 text-2xs text-muted-foreground">
              Code <span className="font-mono">{existing.code}</span> — fixed when it was created; it is
              what exports and the audit log carry.
            </p>
          ) : (
            <p className="mt-1 text-2xs text-muted-foreground">
              The name agents see. A stable code is derived from it on creation.
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="pt-capture-mode">Payment mode</Label>
          <select
            id="pt-capture-mode"
            value={form.captureMode}
            onChange={(e) => patch({ captureMode: e.target.value as CaptureMode })}
            className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-navy-900 disabled:opacity-60"
          >
            {template.captureModes.map((m) => (
              <option key={m.code} value={m.code}>{m.label}</option>
            ))}
          </select>
          <p className="mt-1 text-2xs text-muted-foreground">
            Manual: the agent records what was received. Payment link: a link or QR
            is raised and the gateway confirms.
          </p>
        </div>
      </div>

      <div>
        <Label>Channel type</Label>
        <p className="mb-2 mt-0.5 text-2xs text-muted-foreground">
          How money can arrive through this payment type — the agent picks one of
          these after the type.
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {template.channelTypes.map((c) => (
            <label
              key={c.code}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-white p-2.5 text-sm text-navy-900 hover:bg-navy-50"
            >
              <input
                type="checkbox"
                checked={form.channels.includes(c.code)}
                onChange={() => toggleCode('channels', c.code)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300"
              />
              <span>
                <span className="block font-medium">{c.label}</span>
                <span className="block text-2xs text-muted-foreground">{channelHint(c)}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <Label>Applicable for</Label>
          <div className="mt-1 space-y-1.5">
            {template.applicableFor.map((a) => (
              <label key={a.code} className="flex items-center gap-2 text-sm text-navy-900">
                <input
                  type="checkbox"
                  checked={form.applicableFor.includes(a.code)}
                  onChange={() => toggleCode('applicableFor', a.code)}
                  className="h-4 w-4 rounded border-slate-300"
                />
                {a.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="pt-position">Position</Label>
          <Input
            id="pt-position"
            type="number"
            min={0}
            step={1}
            value={form.position}
            onChange={(e) => patch({ position: e.target.value })}
            required
          />
          <p className="mt-1 text-2xs text-muted-foreground">
            Order in the agent's picker, lowest first.
          </p>
        </div>
      </div>

      <div>
        <Label htmlFor="pt-description">Description</Label>
        <Textarea
          id="pt-description"
          rows={2}
          value={form.description}
          onChange={(e) => patch({ description: e.target.value })}
          placeholder="Current account at the Jaipur branch"
          maxLength={MAX_DESCRIPTION_LENGTH}
        />
      </div>

      {existing && (
        <label className="flex items-center gap-2 text-sm text-navy-900">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => patch({ active: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300"
          />
          Active
        </label>
      )}

      {errorMsg && (
        <Alert variant="destructive">
          <AlertTitle>Could not save</AlertTitle>
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {errors.length > 0 && (
          <span className="mr-auto text-2xs text-muted-foreground">{errors.join(' ')}</span>
        )}
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!canSubmit}>
          {pending ? 'Saving…' : existing ? 'Save changes' : 'Add payment type'}
        </Button>
      </div>
    </form>
  )
}
